/**
 * RatingSelector Component
 *
 * Star-based rating filter for selecting minimum rating.
 * Allows users to filter by minimum guide/experience rating (1-5 stars).
 *
 * SOLID Compliance:
 * - SRP: Only handles rating selection UI
 * - OCP: Extensible via props (maxRating, showLabels)
 * - DIP: Depends on abstractions (onChange callback, not URLStateManager directly)
 *
 * Usage:
 * <RatingSelector
 *   value={4}
 *   onChange={(rating) => updateFilter('minRating', rating)}
 * />
 *
 * Location: apps/web/src/components/search/RatingSelector.jsx
 * Created: 2025-10-31
 * Part of: MVP 1.9 - Adaptive Search & Filter System - Phase 2
 */

import React, { useState } from 'react';
import { Star } from 'lucide-react';

/**
 * RatingSelector Component
 * @param {number} value - Currently selected minimum rating (0-5, 0 = no filter)
 * @param {Function} onChange - Callback when rating changes: onChange(rating)
 * @param {number} maxRating - Maximum rating (default: 5)
 * @param {boolean} showLabels - Show descriptive labels (default: true)
 */
export default function RatingSelector({
  value = 0,
  onChange = () => {},
  maxRating = 5,
  showLabels = true
}) {
  const [hoveredRating, setHoveredRating] = useState(0);

  /**
   * Handle rating selection
   */
  const handleSelect = (rating) => {
    // If clicking the same rating, clear it
    if (value === rating) {
      onChange(0);
    } else {
      onChange(rating);
    }
  };

  /**
   * Get label for rating
   */
  const getRatingLabel = (rating) => {
    const labels = {
      0: 'Any rating',
      1: '1+ stars',
      2: '2+ stars',
      3: '3+ stars',
      4: '4+ stars',
      5: '5 stars only'
    };
    return labels[rating] || '';
  };

  /**
   * Render star icon (filled or empty)
   */
  const renderStar = (index) => {
    const rating = index + 1;
    const displayRating = hoveredRating || value;
    const isFilled = rating <= displayRating;

    return (
      <button
        key={rating}
        onClick={() => handleSelect(rating)}
        onMouseEnter={() => setHoveredRating(rating)}
        onMouseLeave={() => setHoveredRating(0)}
        className="group relative transition-transform hover:scale-110 focus:outline-none"
        aria-label={`${rating} star${rating > 1 ? 's' : ''}`}
      >
        <Star
          className={`w-8 h-8 transition-colors ${
            isFilled
              ? 'fill-yellow-400 text-yellow-400'
              : 'fill-gray-200 text-gray-300 group-hover:text-gray-400'
          }`}
        />
      </button>
    );
  };

  return (
    <div className="w-full">
      {/* Rating Label */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-gray-900">
          MINIMUM RATING
        </div>
        {value > 0 && (
          <button
            onClick={() => onChange(0)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Clear
          </button>
        )}
      </div>

      {/* Star Rating Selector */}
      <div className="flex items-center gap-2 mb-3">
        {Array.from({ length: maxRating }, (_, index) => renderStar(index))}
      </div>

      {/* Rating Description */}
      {showLabels && (
        <div className="text-sm text-gray-600">
          {getRatingLabel(hoveredRating || value)}
        </div>
      )}

      {/* Selected Rating Display */}
      {value > 0 && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="flex">
              {Array.from({ length: value }, (_, i) => (
                <Star
                  key={i}
                  className="w-4 h-4 fill-yellow-400 text-yellow-400"
                />
              ))}
            </div>
            <span className="text-sm text-blue-900 font-medium">
              {value}+ star minimum
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
